import {
  CreateStoryboardParams,
  useCreateStoryboard,
} from "@/hooks/storyboards/useCreateStoryboard";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { fetchFileTree } from "@/store/codebaseSlice";
import { fetchGitStatus } from "@/store/gitStatusSlice";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { ReactFramework } from "@/utils/templateProjects";
import {
  getUnexpiredToken,
  imageStoryboardTemplateNextJs,
  imageStoryboardTemplateReact,
  useActiveCanvas,
  useActiveProject,
  useAutoUpdateRef,
  useCanvasId,
} from "@/utils/utils";
import { parse } from "recast";
import * as recastBabelTSParser from "recast/parsers/babel-ts";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { useSession } from "@clerk/clerk-react";
import { useReactComponentHandler } from "@/hooks/useReactComponentHandler";
import { useLoadCodebase, useStartServer } from "@/utils/devServerUtils";
import useActivityStore from "@/store/zustand/activityStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  ClipboardData,
  ExtensionMetadata,
  getTempoClipboardData,
  getExtensionClipboardMetadata,
  setTempoClipboardData,
  useInsertClipboardDataIntoCanvas,
} from "../utils/canvasClipboardData";
import { PasteOptionsModal } from "@/components/PasteOptionsModal";
import { atob_utf8 } from "@/utils/bytes";
import { compressNavTree } from "@/utils/navTreeCompression";
import { convertHtmlToJsx, getContentsWithoutShell } from "../IframePool/utils";
import { useIframePoolStore } from "../IframePool/iframePoolStore";
import {
  createDummyComponent,
  wrapWithBlankStoryboard,
} from "@/components/AppHeader/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { NavTreeNode, Storyboard } from "@/utils/types";
import { isTargetTextInput } from "@/utils/clipboardUtils";
import { useExtractElementStore } from "../stores/extractElementStore";
import { getComponentFileFromNavTreeNodes } from "@/utils/jsx/extractElementsUtils";
import _ from "lodash";
import { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { AddAndRemoveJsxChange } from "@/store/zustand/changeLedgerTypes";
import { processInstantUpdateData } from "@/utils/navTreeUtils";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { v4 as uuidv4 } from "uuid";
import { usePagesStore } from "@/store/zustand/pagesStore";

export const TEMPO_ELEMENT_PASTE_STORYBOARD_WIDTH = 800;
export const TEMPO_ELEMENT_PASTE_STORYBOARD_HEIGHT = 600;

enum CopyType {
  Element,
  Storyboard,
}

/**
 * Validates if a given code string represents a valid React component.
 * Checks for:
 * - Function components (both regular and arrow functions)
 * - Class components with render method
 * - Proper component naming (PascalCase)
 * - JSX return statements
 *
 * @param {string} code - The code string to validate
 * @returns {Promise<boolean>} True if the code is a valid React component, false otherwise
 * @throws {Error} If the code cannot be parsed as valid JavaScript/TypeScript
 */
export const validateCode = async (code: string): Promise<boolean> => {
  try {
    const ast = parse(code, {
      parser: recastBabelTSParser,
    });

    let hasReactComponent = false;
    const componentDeclarations = new Map<string, any>();

    // First pass: collect all declarations that could be components
    for (const node of ast.program.body) {
      if (node.type === "VariableDeclaration") {
        for (const declaration of node.declarations) {
          if (
            declaration.id &&
            declaration.id.type === "Identifier" &&
            declaration.init &&
            (declaration.init.type === "ArrowFunctionExpression" ||
              declaration.init.type === "FunctionExpression")
          ) {
            const componentName = declaration.id.name;
            if (
              componentName &&
              componentName[0] === componentName[0].toUpperCase()
            ) {
              componentDeclarations.set(componentName, declaration.init);
            }
          }
        }
      } else if (node.type === "ClassDeclaration") {
        if (
          node.id &&
          node.id.name &&
          node.id.name[0] === node.id.name[0].toUpperCase()
        ) {
          componentDeclarations.set(node.id.name, node);
        }
      }
    }

    // Second pass: look for exports and validate components
    for (const node of ast.program.body) {
      if (node.type === "ExportDefaultDeclaration") {
        const declaration = node.declaration;

        if (declaration) {
          // Direct function export
          if (declaration.type === "FunctionDeclaration") {
            const componentName = declaration.id?.name;
            if (
              componentName &&
              componentName[0] === componentName[0].toUpperCase()
            ) {
              hasReactComponent = checkIfValidReactFunction(declaration);
            }
          } else if (declaration.type === "ClassDeclaration") {
            if (
              declaration.id &&
              declaration.id.name &&
              declaration.id.name[0] === declaration.id.name[0].toUpperCase()
            ) {
              hasReactComponent = checkIfValidReactClass(declaration);
            }
          } else if (declaration.type === "Identifier") {
            // Handle case like: export default ComponentName
            const exportedName = declaration.name;
            if (exportedName && componentDeclarations.has(exportedName)) {
              const componentNode = componentDeclarations.get(exportedName);
              if (componentNode.type === "ClassDeclaration") {
                hasReactComponent = checkIfValidReactClass(componentNode);
              } else {
                hasReactComponent = checkIfValidReactFunction(componentNode);
              }
            }
          }
        }
      } else if (node.type === "ExportNamedDeclaration") {
        const declaration = node.declaration;
        if (declaration && declaration.type === "FunctionDeclaration") {
          const componentName = declaration.id?.name;
          if (
            componentName &&
            componentName[0] === componentName[0].toUpperCase()
          ) {
            hasReactComponent = checkIfValidReactFunction(declaration);
          }
        }
      }

      if (hasReactComponent) break;
    }

    return hasReactComponent;
  } catch (error) {
    return false;
  }
};

/**
 * Validates if a given string represents valid HTML content.
 * Checks for basic HTML structure and format.
 *
 * @param {string} text - The text string to validate as HTML
 * @returns {boolean} True if the text appears to be HTML, false otherwise
 */
const validateHtml = (text: string): boolean => {
  if (!text || typeof text !== "string") {
    return false;
  }

  // Trim the text to check
  const trimmedText = text.trim();

  // Check for common HTML patterns
  // Look for opening and closing HTML tags
  const htmlTagPattern = /<\/?[a-zA-Z][^>]*>/;
  const hasHtmlTags = htmlTagPattern.test(trimmedText);

  // Check for DOCTYPE or html tag (full document)
  const isFullDocument =
    /<!DOCTYPE\s+html/i.test(trimmedText) || /<html[^>]*>/i.test(trimmedText);

  // Check for common HTML elements
  const hasBodyTag = /<body[^>]*>/i.test(trimmedText);

  // Consider it HTML if it has HTML tags and either is a full document or has common elements
  return hasHtmlTags && (isFullDocument || hasBodyTag);
};

const validateSvg = (text: string): boolean => {
  if (!text || typeof text !== "string") {
    return false;
  }

  const trimmedText = text.trim();

  const hasSvgStart = /<svg[\s>]/i.test(trimmedText);
  const hasSvgEnd = /<\/svg>/i.test(trimmedText);

  if (!hasSvgStart || !hasSvgEnd) {
    return false;
  }

  const svgMatch = trimmedText.match(/<svg[\s\S]*?<\/svg>/i);
  if (!svgMatch) {
    return false;
  }

  try {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgMatch[0], "image/svg+xml");
    const parseError = svgDoc.querySelector("parsererror");
    return !parseError;
  } catch {
    return false;
  }
};

export const useDuplicateSelectedElements = () => {
  const { selectedElement, selectedNavTreeNode } = useSelectedNavTreeNode();
  const { insertClipboardDataIntoCanvas } = useInsertClipboardDataIntoCanvas();

  const getCopyDataForSelectedElements = useGetCopyDataForSelectedElements();

  return useCallback(async () => {
    if (!selectedElement?.isKnownElement() || !selectedNavTreeNode) {
      return;
    }

    try {
      const copyData = await getCopyDataForSelectedElements();
      if (!copyData) {
        return;
      }

      const duplicateData: ClipboardData = {
        ...copyData,
        width: copyData.width,
        height: copyData.height,
      };

      await insertClipboardDataIntoCanvas(duplicateData);

      toast.success("Element duplicated");
    } catch (error) {
      console.error("Error duplicating elements:", error);
      toast.error("Failed to duplicate elements");
    }
  }, [
    selectedElement,
    selectedNavTreeNode,
    getCopyDataForSelectedElements,
    insertClipboardDataIntoCanvas,
  ]);
};

export const usePasteToReplace = () => {
  const { selectedElement } = useSelectedNavTreeNode();
  const { multiselectedElements } = useMultiselectedNavTreeNode();
  const activeCanvas = useActiveCanvas();
  const canvasRef = useAutoUpdateRef(activeCanvas);

  return useCallback(async () => {
    try {
      // Read tempo metadata from clipboard
      const clipboardItems = await navigator.clipboard.read();
      let tempoClipboardData: ClipboardData | null = null;

      for (const item of clipboardItems) {
        if (item.types.includes("text/html")) {
          const blob = await item.getType("text/html");
          const html = await blob.text();
          const match = html.match(/<!--tempo-metadata:([^>]+)-->/);
          if (match) {
            tempoClipboardData = JSON.parse(atob_utf8(match[1]));
            break;
          }
        }
      }

      if (!tempoClipboardData) {
        toast.error("No element copied. Copy an element first.");
        return;
      }

      const targets = (
        multiselectedElements?.length
          ? multiselectedElements
          : selectedElement
            ? [selectedElement]
            : []
      ).filter((t) => t && !t.isEmpty());

      if (!targets.length) {
        toast.error("No element selected to replace");
        return;
      }

      const {
        fileContents,
        fileContentsSourceFilename,
        compressedNavTreeNodesForInstantUpdate,
        htmlForInstantUpdate,
      } = tempoClipboardData;

      if (!fileContents || !canvasRef.current?.id) return;

      const narrowedCompressed = compressedNavTreeNodesForInstantUpdate?.length
        ? [compressedNavTreeNodesForInstantUpdate[0]]
        : compressedNavTreeNodesForInstantUpdate;

      const codebaseIdHashingKey = uuidv4();
      const replacedElementKeys: string[] = [];
      const changes: AddAndRemoveJsxChange[] = [];

      // Create all changes first
      for (const target of targets) {
        const {
          navTree: processedNavTree,
          html: processedHtml,
          error,
        } = processInstantUpdateData({
          storyboardId: target.storyboardId,
          canvasId: canvasRef.current!.id,
          projectSubfolderPath: undefined,
          instantUpdateData: {
            html: htmlForInstantUpdate ?? "",
            compressedNavTreeNodes: narrowedCompressed,
          },
          wrapInShell: false,
        });

        if (error) continue;

        const change = new AddAndRemoveJsxChange({
          addJsxChangeFields: {
            fileContentsToSourceFrom: fileContents,
            fileContentsSourceFilename:
              fileContentsSourceFilename || "index.tsx",
            htmlForInstantUpdate: processedHtml,
            navTreeNodesForInstantUpdate: processedNavTree
              ? [processedNavTree]
              : undefined,
            codebaseIdHashingKey,
          },
          removeJsxChangeFields: {},
          replaceCodebaseId: target.codebaseId,
        });

        changes.push(change);
        replacedElementKeys.push(target.getKey());
      }

      if (!changes.length) return;

      // Execute all changes
      changes.forEach((change) => {
        useDesignCommandManager.getState().executeCommand(change);
      });

      // Set selection once after all changes to avoid jumping
      const firstKey = replacedElementKeys[0];
      const multiKeys =
        replacedElementKeys.length > 1 ? replacedElementKeys : null;

      if (firstKey && changes.length > 0) {
        const lastChange = changes[changes.length - 1];
        lastChange.setSelectedElementsAfterInstantUpdate(firstKey, multiKeys);
      }
    } catch (error) {
      console.error("Error accessing clipboard:", error);
      toast.error("Failed to access clipboard");
    }
  }, [multiselectedElements, selectedElement, canvasRef]);
};

export const useGetCopyDataForSelectedElements = () => {
  const { selectedElement, selectedNavTreeNode } = useSelectedNavTreeNode();
  const { multiselectedElements } = useMultiselectedNavTreeNode();
  const { multiselectedNavTreeNodes } = useMultiselectedNavTreeNode();

  const canvasId = useCanvasId();
  const canvas = useActiveCanvas();

  return useCallback(async (): Promise<ClipboardData | null> => {
    if (!selectedElement?.isKnownElement() || !selectedNavTreeNode) {
      return null;
    }

    const selectedElementBoundingBox =
      useExtractElementStore.getState().selectedElementBoundingBox;

    // Get source storyboard name to preserve it when pasting
    const sourceStoryboard = canvas?.storyboards?.find(
      (sb) => sb.id === selectedElement.storyboardId,
    );
    const sourceStoryboardName = sourceStoryboard?.name;

    const navTreeNodes = _.cloneDeep(
      multiselectedNavTreeNodes?.length
        ? multiselectedNavTreeNodes
        : [selectedNavTreeNode],
    );

    // Create a dummy html so we can also clear the classes on the html element themselves
    // Note that if there are no classes to remove, we don't need to create the dummy html
    let dummyParent: HTMLElement | null = null;
    const removeHtmlClassesForNode = (
      navNode: NavTreeNode,
      index: number,
      classesToRemove: Set<string>,
    ) => {
      if (!dummyParent) {
        const dummyHtml = `<div id="tempo-parent-for-copy-paste">${
          useExtractElementStore.getState().selectedElementHTML ?? ""
        }</div>`;
        const dom = new DOMParser().parseFromString(dummyHtml, "text/html");
        dummyParent = dom.getElementById("tempo-parent-for-copy-paste")!;
      }

      const node = dummyParent.children[index] as HTMLElement;
      // Check if the node has the correct codebase id in class list
      if (
        node.getAttribute("data-tempoelementid") ===
        navNode.tempoElement.codebaseId
      ) {
        node.classList.remove(...classesToRemove);
      }
    };

    const getHtmlForInstantUpdate = () => {
      if (dummyParent) {
        return dummyParent.innerHTML;
      }

      return useExtractElementStore.getState().selectedElementHTML ?? "";
    };

    // Clear everything related to absolute positioning as it is not relevant to wherever we are copying or moving to
    const treeElementOverrides: { [codebaseId: string]: JsxElement } = {};
    const treeElementLookup = useCodebaseStore.getState().treeElementLookup;
    navTreeNodes?.forEach((navNode) => {
      const classesToRemove = new Set(
        navNode.classList?.filter((cls) => {
          if (cls === "absolute" || cls === "fixed") {
            return true;
          }

          if (
            cls.startsWith("top-") ||
            cls.startsWith("bottom-") ||
            cls.startsWith("left-") ||
            cls.startsWith("right-") ||
            cls.startsWith("inset-") ||
            cls.startsWith("start-") ||
            cls.startsWith("end-")
          ) {
            return true;
          }

          return false;
        }) ?? [],
      );

      navNode.classList = navNode.classList?.filter(
        (cls) => !classesToRemove.has(cls),
      );

      if (navNode.props?.className?.value) {
        navNode.props.className.value = navNode.props.className.value
          .split(" ")
          .filter((cls) => !classesToRemove.has(cls))
          .join(" ");
      }

      if (treeElementLookup[navNode.tempoElement.codebaseId]) {
        const treeElement = _.cloneDeep(
          treeElementLookup[navNode.tempoElement.codebaseId],
        );
        treeElement.removableClasses = treeElement.removableClasses?.filter(
          (cls) => !classesToRemove.has(cls),
        );
        if (typeof treeElement.setProps?.className?.value === "string") {
          treeElement.setProps.className.value =
            treeElement.setProps.className.value
              .split(" ")
              .filter((cls) => !classesToRemove.has(cls))
              .join(" ");
        }
        treeElementOverrides[navNode.tempoElement.codebaseId] = treeElement;
      }

      if (classesToRemove.size > 0) {
        removeHtmlClassesForNode(
          navNode,
          navTreeNodes.indexOf(navNode),
          classesToRemove,
        );
      }
    });

    const {
      newFileContents: fileContents,
      dummyFilePath: fileContentsSourceFilename,
    } =
      (await getComponentFileFromNavTreeNodes(
        canvasId,
        navTreeNodes,
        false,
        treeElementOverrides,
      )) ?? {};

    return {
      selectedElementKey: selectedElement.getKey(),
      multiselectedElementKeys: multiselectedElements?.map((element) =>
        element.getKey(),
      ),

      fileContents,
      fileContentsSourceFilename: fileContentsSourceFilename ?? undefined,

      htmlForInstantUpdate: getHtmlForInstantUpdate(),
      compressedNavTreeNodesForInstantUpdate: navTreeNodes
        .map((node) => compressNavTree(node))
        .filter((result) => result !== null),

      width: selectedElementBoundingBox?.width || 0,
      height: selectedElementBoundingBox?.height || 0,

      sourceParentElementKey:
        selectedNavTreeNode?.parent?.tempoElement?.getKey(),

      sourceStoryboardName,
    };
  }, [
    selectedElement,
    multiselectedElements,
    selectedNavTreeNode,
    multiselectedNavTreeNodes,
    canvasId,
    canvas,
  ]);
};

export const useGetCopyDataForSelectedStoryboards = () => {
  const canvasId = useCanvasId();
  const canvas = useActiveCanvas();
  const storyboardToNavTree = useCanvasStore(
    (store) => store.storyboardToNavTree,
  );

  return useCallback(
    async (storyboardIds: string[]): Promise<ClipboardData | null> => {
      const storyboards = canvas?.storyboards?.filter((sb) =>
        storyboardIds.includes(sb.id),
      );
      if (!storyboards?.length) {
        return null;
      }

      // Filter out STORY type - cannot be copied
      const copyableStoryboards = storyboards.filter(
        (sb) => sb.type !== STORYBOARD_TYPES.STORY,
      );

      if (!copyableStoryboards.length) {
        toast.info("Story storyboards cannot be copied");
        return null;
      }

      // Build data for each storyboard (store raw positions, calculate offsets at paste time)
      const storyboardsData: NonNullable<ClipboardData["storyboards"]> =
        await Promise.all(
          copyableStoryboards.map(async (sb) => {
            const data: NonNullable<ClipboardData["storyboards"]>[0] = {
              type: sb.type,
              name: sb.name,
              width: sb.width,
              height: sb.height,
              x: sb.x, // Raw position
              y: sb.y,
              zIndex: sb.zIndex,
            };

            if (sb.type === STORYBOARD_TYPES.APPLICATION) {
              data.route = sb.route;
            } else if (sb.type === STORYBOARD_TYPES.COMPONENT) {
              // Get nav tree for this storyboard
              const navTree = storyboardToNavTree[sb.id];

              if (navTree) {
                // Use same approach as element copy - generate file from nav tree
                const res = await getComponentFileFromNavTreeNodes(
                  canvasId,
                  [navTree],
                  false,
                );

                if (res?.newFileContents) {
                  data.fileContents = res.newFileContents;
                  data.fileContentsSourceFilename =
                    res.dummyFilePath ?? undefined;
                }

                const compressed = compressNavTree(navTree);
                if (compressed) data.compressedNavTree = compressed;
              }

              // Get HTML for instant update - strip the shell to match element copy behavior
              // (shell will be re-added on paste via processInstantUpdateData with wrapInShell: true)
              const htmlFromSource =
                useIframePoolStore.getState().htmlForStoryboard[sb.id];
              if (htmlFromSource) {
                data.htmlForInstantUpdate =
                  getContentsWithoutShell(htmlFromSource);
              }

              data.importName = sb.storyComponentName;
            }

            return data;
          }),
        );

      return {
        storyboards: storyboardsData,
        // Legacy fields for backwards compat (first storyboard)
        sourceStoryboardName: copyableStoryboards[0].name,
        width: copyableStoryboards[0].width,
        height: copyableStoryboards[0].height,
      };
    },
    [canvas, canvasId, storyboardToNavTree],
  );
};

const checkIfValidReactFunction = (functionNode: any): boolean => {
  if (
    functionNode.type === "FunctionDeclaration" ||
    functionNode.type === "ArrowFunctionExpression" ||
    functionNode.type === "FunctionExpression"
  ) {
    if (functionNode.body && functionNode.body.type === "BlockStatement") {
      for (const statement of functionNode.body.body) {
        if (
          statement.type === "ReturnStatement" &&
          statement.argument &&
          statement.argument.type === "JSXElement"
        ) {
          return true;
        }
      }
    } else if (functionNode.body && functionNode.body.type === "JSXElement") {
      return true;
    }
  }
  return false;
};

const checkIfValidReactClass = (classDeclaration: any): boolean => {
  const renderMethod = classDeclaration.body.body.find(
    (member: any) =>
      member.type === "ClassMethod" &&
      member.key.type === "Identifier" &&
      member.key.name === "render",
  );
  if (
    renderMethod &&
    renderMethod.type === "ClassMethod" &&
    renderMethod.body &&
    renderMethod.body.type === "BlockStatement"
  ) {
    for (const statement of renderMethod.body.body) {
      if (
        statement.type === "ReturnStatement" &&
        statement.argument &&
        statement.argument.type === "JSXElement"
      ) {
        return true;
      }
    }
  }
  return false;
};

const createImageStoryboardContents = (
  pathToImage: string,
  altText: string,
  naturalWidth: number,
  naturalHeight: number,
  framework?: ReactFramework,
) => {
  if (pathToImage.startsWith("public")) {
    pathToImage = pathToImage.replace("public", "");
  }

  return framework === ReactFramework.NEXT_JS ||
    framework === ReactFramework.NEXT_JS_APP_ROUTER
    ? imageStoryboardTemplateNextJs(
        pathToImage,
        naturalWidth,
        naturalHeight,
        altText,
      )
    : imageStoryboardTemplateReact(
        pathToImage,
        naturalWidth,
        naturalHeight,
        altText,
      );
};

const createImageStoryboardInstantUpdateHtml = (
  pathToImage: string,
  altText: string,
  naturalWidth?: number,
  naturalHeight?: number,
) => {
  return `<img id="${pathToImage}" src="${pathToImage}" alt="${altText}" width="${naturalWidth}" height="${naturalHeight}" />`;
};

export const useCreateImageStoryboard = () => {
  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();
  const project = useActiveProject();
  const canvasId = useCanvasId();
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );

  return async ({
    filePath,
    positionX,
    positionY,
    actionText,
    width,
    height,
    altText,
  }: {
    filePath: string;
    positionX: number;
    positionY: number;
    width: number;
    height: number;
    altText: string;
    actionText: string;
  }) => {
    if (!selectedPageId) {
      toast.error("Please select a page before creating a storyboard");
      return;
    }

    const newStoryboard = initializeStoryboard(
      positionX,
      positionY,
      width,
      height,
    );

    await createStoryboard({
      storyboard: newStoryboard,
      pageId: selectedPageId,
      fileContentsToCreateWith: createImageStoryboardContents(
        filePath,
        altText,
        width,
        height,
        project?.framework as ReactFramework,
      ),
      actionText,
      instantUpdateData: {
        html: createImageStoryboardInstantUpdateHtml(
          filePath,
          altText,
          width,
          height,
        ),
      },
    });
  };
};

export const useHandleImagePaste = () => {
  const canvasId = useCanvasId();
  const dispatch = useDispatch<any>();
  const project = useActiveProject();
  const canvas = useActiveCanvas();

  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const { session } = useSession();

  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );

  /**
   * Takes an image item from the clipboard, creates a new file in an assets directory, then creates a storyboard with the a image rendered with the image.
   * @param blob
   * @param options - Options for the image paste.
   */
  const handleImagePaste = useCallback(
    async (
      blob: File,
      options: {
        position?: { x: number; y: number };
      } = {},
    ) => {
      if (!session) {
        return;
      }

      try {
        const mimeType = blob.type;
        let extension = mimeType.split("/")[1];
        if (mimeType === "image/svg+xml") {
          extension = "svg";
        }

        // Create a timestamp-based file name
        const timestamp = new Date().toISOString().replace(/[-:.]/g, ""); // ISO string with no special characters
        const assetDir = "public/images/";
        const basename = `tempo-image-${timestamp}.${extension}`;
        const fileName = `${assetDir}${basename}`;

        const file = new File([blob], basename, { type: mimeType });
        useCanvasStore.getState().addTemporaryFile({ id: fileName, file });

        const { x, y } = useCanvasStore.getState().canvasMousePos;

        let positionX = x;
        let positionY = y;

        if (options.position) {
          positionX = options.position.x;
          positionY = options.position.y;
        }

        try {
          const tempUrl = URL.createObjectURL(blob);

          // Create a new Image object to load the blob
          const img = new Image();
          img.onload = async () => {
            // Get the dimensions of the image
            const loadedWidth = img.naturalWidth;
            const loadedHeight = img.naturalHeight;

            const finalWidth =
              loadedWidth > 0
                ? loadedWidth
                : TEMPO_ELEMENT_PASTE_STORYBOARD_WIDTH;
            const finalHeight =
              loadedHeight > 0
                ? loadedHeight
                : TEMPO_ELEMENT_PASTE_STORYBOARD_HEIGHT;

            try {
              if (!selectedPageId) {
                toast.error("Please select a page before pasting an image");
                return;
              }

              const newStoryboard = initializeStoryboard(
                positionX,
                positionY,
                finalWidth,
                finalHeight,
              );

              await createStoryboard({
                storyboard: newStoryboard,
                pageId: selectedPageId,
                fileContentsToCreateWith: createImageStoryboardContents(
                  fileName,
                  "Pasted Image",
                  finalWidth,
                  finalHeight,
                  project?.framework as ReactFramework,
                ),
                actionText: "Pasted Image",
                instantUpdateData: {
                  html: createImageStoryboardInstantUpdateHtml(
                    fileName,
                    "Pasted Image",
                    finalWidth,
                    finalHeight,
                  ),
                },
              });
            } catch (error) {
              toast.error("Couldn't paste image: ", error);
            }
          };
          // Set the src of the image to the temporary URL to start loading
          img.src = tempUrl;
        } catch (error) {
          toast.error("Couldn't paste image: ", error);
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("directoryToUploadTo", assetDir);

        const supabaseJWT = await getUnexpiredToken(session);

        const res = await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/createFile`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        if (res?.data) {
          useCodebaseStore
            .getState()
            .updateLookupsBasedOnPartialResponse(res.data);
          useActivityStore.getState().triggerRefetchActivityStream();
        }

        dispatch(
          fetchGitStatus({
            session,
            canvasId,
          }),
        );

        dispatch(
          fetchFileTree({
            session,
            canvasId,
          }),
        );

        setTimeout(() => {
          useCanvasStore.getState().removeTemporaryFile(basename);
        }, 5000);

        toast.success("Uploaded file");
      } catch (error) {
        console.error("Error processing pasted image:", error);
        toast.error("Error processing image: " + error.message);
        throw error;
      }
    },
    [
      canvasId,
      dispatch,
      project,
      initializeStoryboard,
      createStoryboard,
      session,
      selectedPageId,
    ],
  );

  return handleImagePaste;
};

/**
 * Unified handler for pasting HTML or React code content.
 * Determines if text is HTML or valid React code, then creates a storyboard.
 */
const handlePasteCodeOrHtml = async ({
  text,
  mouseX,
  mouseY,
  actionTextSuffix,
  project,
  nonShadcnFiles,
  initializeStoryboard,
  createStoryboard,
  showFancyWarning,
  selectedPageId,
}: {
  text: string;
  mouseX: number;
  mouseY: number;
  actionTextSuffix: string;
  project: any;
  nonShadcnFiles: { [key: string]: string };
  initializeStoryboard: (x: number, y: number, w: number, h: number) => any;
  createStoryboard: (
    options: CreateStoryboardParams,
  ) => Promise<Storyboard | null>;
  showFancyWarning: boolean;
  selectedPageId: string;
}): Promise<Storyboard | null> => {
  // Check if it's HTML first
  if (validateHtml(text)) {
    try {
      if (!selectedPageId) {
        toast.error("Please select a page before pasting HTML");
        return null;
      }

      const bodyContents = getContentsWithoutShell(text, true);
      const jsx = convertHtmlToJsx(bodyContents);

      if (!jsx) {
        toast.warning("Could not extract HTML body content.");
        return null;
      }

      const wrappedJsx = wrapWithBlankStoryboard(
        jsx,
        project?.framework as ReactFramework,
        1200,
        800,
      );

      const componentCode = createDummyComponent(
        wrappedJsx,
        project?.framework as ReactFramework,
        "PastedHtml",
      );

      const newStoryboard = initializeStoryboard(mouseX, mouseY, 1200, 800);
      const actionText = actionTextSuffix
        ? `Pasted HTML ${actionTextSuffix}`
        : "Pasted HTML";

      const createdStoryboard = await createStoryboard({
        storyboard: newStoryboard,
        fileContentsToCreateWith: componentCode,
        pageId: selectedPageId,
        actionText,
        extraPathToContents: nonShadcnFiles,
      });
      return createdStoryboard;
    } catch (error) {
      toast.error(
        "Couldn't paste HTML: " +
          (error instanceof Error ? error.message : String(error)),
      );
      return null;
    }
  }

  // Check if it's valid React code
  const isValid = await validateCode(text);

  if (isValid) {
    try {
      if (!selectedPageId) {
        toast.error("Please select a page before pasting code");
        return null;
      }

      const newStoryboard = initializeStoryboard(mouseX, mouseY, 1200, 800);
      newStoryboard.resizeOnceRendered = true;
      const actionText = actionTextSuffix
        ? `Pasted Code ${actionTextSuffix}`
        : "Pasted Code";

      const createdStoryboard = await createStoryboard({
        storyboard: newStoryboard,
        fileContentsToCreateWith: text,
        actionText,
        extraPathToContents: nonShadcnFiles,
        pageId: selectedPageId,
      });
      return createdStoryboard;
    } catch (error) {
      toast.error(
        "Couldn't paste: " +
          (error instanceof Error ? error.message : String(error)),
      );
      return null;
    }
  }

  // Neither HTML nor valid React code
  if (showFancyWarning) {
    toast.warning("Clipboard contents must be a valid React component.", {
      action: {
        label: "Learn more at react.dev",
        onClick: () =>
          window.open(
            "https://react.dev/learn/your-first-component",
            "_blank",
            "noopener,noreferrer",
          ),
      },
      classNames: {
        toast: "flex flex-col items-start justify-center gap-2",
        actionButton: "w-full justify-center",
      },
    });
  } else {
    toast.warning("Clipboard contents must be a valid React component.");
  }
  return null;
};

/**
 * Component used to handle side-effects that should not re-render the app
 * but are related to the hook.
 *
 * Note, if the hook is called in multiple places this component will be initialized in
 * multiple places as well
 */
export const CopyPasteHandler: React.FC = React.memo(() => {
  const { session } = useSession();

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const elementKeyToNavNodeLookup = useCanvasStore(
    (store) => store.elementKeyToNavNodeLookup,
  );

  const project = useActiveProject();
  const canvas = useActiveCanvas();

  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );

  const { selectedNavTreeNode, selectedElement, storyboardIdSelected } =
    useSelectedNavTreeNode();
  const { hoveredElement } = useHoveredNavTreeNode();
  const { multiselectedStoryboardIds } = useMultiselectedNavTreeNode();

  const layoutMode = useLayoutStore((store) => store.layoutMode);
  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();
  const canvasId = useCanvasId();
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );

  const handleImagePaste = useHandleImagePaste();

  const { installDepsForComponent } = useReactComponentHandler();
  const installDepsForComponentRef = useAutoUpdateRef(installDepsForComponent);

  const { insertClipboardDataIntoCanvas } = useInsertClipboardDataIntoCanvas();

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [pastedHtml, setPastedHtml] = React.useState<string>("");
  const [modalHandlers, setModalHandlers] = React.useState<{
    onPasteOriginal: () => Promise<Storyboard | null>;
    onCleanWithAI: (storyboard: Storyboard | null) => Promise<void>;
  } | null>(null);

  const handlePaste = useCallback(
    async (e: ClipboardEvent) => {
      // Check if we're pasting into an input context
      const target = e.target as HTMLElement;
      // skip handling paste if the target is an input, textarea, code, or part of a Monaco editor or PRD text editor (aka normal paste)
      if (isTargetTextInput(target)) {
        return;
      }

      if (!canvasRef.current || !selectedPageId) {
        return;
      }

      // @ts-ignore
      const clipboardData = e.clipboardData || window.clipboardData;

      // Look for an image type in the clipboard items
      let imageBlob: File | null = null;
      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i];
        // Check if any of the types are of an image
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            imageBlob = file;
            break;
          }
        }
      }

      if (imageBlob) {
        try {
          await handleImagePaste(imageBlob);
        } catch (error) {
          // Error already logged and toasted by handleImagePaste
        }
        return;
      }

      // Check for SVG text content in clipboard
      const clipboardText = clipboardData.getData("text/plain") || "";
      if (clipboardText && validateSvg(clipboardText)) {
        try {
          const trimmedText = clipboardText.trim();
          const svgMatch = trimmedText.match(/<svg[\s\S]*?<\/svg>/i);
          const svgContent = svgMatch ? svgMatch[0] : trimmedText;

          const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
          const basename = `tempo-svg-${timestamp}.svg`;

          const svgBlob = new Blob([svgContent], { type: "image/svg+xml" });
          const svgFile = new File([svgBlob], basename, {
            type: "image/svg+xml",
          });

          await handleImagePaste(svgFile);
        } catch (error) {
          console.error("Error processing pasted SVG:", error);
          toast.error("Error processing pasted SVG");
        }
        return;
      }

      // Check for chrome extension metadata first (before tempo copy data)
      const extensionMetadata = await getExtensionClipboardMetadata(e);

      if (extensionMetadata?.source === "tempo-chrome-extension") {
        const nonShadcnFiles: { [key: string]: string } = {};

        // Get the raw HTML content specifically for preview
        let htmlPreviewContent = "";
        if (e.clipboardData) {
          htmlPreviewContent = e.clipboardData.getData("text/html");
        }

        // Get the text content for processing
        let text = await getPastedText();
        if (!text && (e.clipboardData || (window as any).clipboardData)) {
          text = (e.clipboardData || (window as any).clipboardData).getData(
            "text/plain",
          );
        }

        // Use the HTML content for preview if available, otherwise fallback to text
        const contentForPreview = htmlPreviewContent || text;

        // Strip metadata wrapper from HTML if present
        const stripMetadataWrapper = (html: string): string => {
          // Remove metadata comment
          let cleaned = html.replace(
            /<!--tempo(?:-chrome)?-extension-metadata:[\s\S]*?-->/,
            "",
          );
          // Remove wrapper div with data-tempo-(chrome-)extension attribute
          cleaned = cleaned.replace(
            /<div data-tempo(?:-chrome)?-extension="[^"]*" style="display:contents;">([\s\S]*)<\/div>$/,
            "$1",
          );
          return cleaned.trim();
        };

        if (contentForPreview) {
          const cleanedPreview = stripMetadataWrapper(contentForPreview);
          setPastedHtml(cleanedPreview);

          if (text) {
            text = stripMetadataWrapper(text);
          } else {
            text = cleanedPreview;
          }

          const processPaste = async (
            pasteText: string,
          ): Promise<Storyboard | null> => {
            const { x: mouseX, y: mouseY } =
              useCanvasStore.getState().canvasMousePos;

            return await handlePasteCodeOrHtml({
              text: pasteText,
              mouseX,
              mouseY,
              actionTextSuffix: "from Extension",
              project,
              nonShadcnFiles,
              initializeStoryboard,
              createStoryboard,
              showFancyWarning: false,
              selectedPageId,
            });
          };

          const handlePasteOriginal = async (): Promise<Storyboard | null> => {
            return await processPaste(text);
          };

          const handleCleanWithAI = async (
            storyboard: Storyboard | null,
          ): Promise<void> => {
            await new Promise((resolve) => setTimeout(resolve, 300));

            const storyboardFilePath = storyboard?.fullFilePath;

            const prompt = `Refactor the file ${storyboardFilePath} into well-structured React components while keeping the exact same visual appearance.`;

            const additionalInstructions = `GOAL: Transform this large HTML/React file into a clean, maintainable structure where the main file is primarily composition - importing and arranging smaller, reusable components.

  ### WHAT SUCCESS LOOKS LIKE:
  - The main file should be mostly imports and component composition 
  - Repeated patterns become shared components
  - Large sections (headers, forms, cards, lists, etc.) become their own files
  - Each extracted component is self-contained with its original styles intact

  ### APPROACH:
  - Work through the file systematically, top to bottom
  - Extract components to src/components/ (follow existing conventions)
  - IMMEDIATELY after creating a component, import it in the main file and replace the original code with the <Component />
  - Verify the edit was applied correctly before continuing to creating the next component
  - For very large files, process in sections to avoid errors

  ### VALIDATION:
  - Run type checks (tsc --noEmit) periodically to catch errors early
  - Fix any build-blocking errors before continuing

  ## FILE LOCATIONS:
  - Create new components in src/components/ (check existing folder structure).
  - DO NOT create in tempobook/storyboards/.

  ## SAFETY:
  - NEVER delete and recreate the main file.
  - If a replacement breaks syntax, UNDO or FIX it immediately.
  
  CRITICAL: Preserve exact visual appearance. All className values and styles must remain unchanged - the output must look identical to the original.`;

            const sent = useAiContextStore.getState().setSendChatMessage({
              sendChatMessage: prompt,
              sendChatMessageFiles: undefined,
              additionalInstructions,
              criticalFiles: storyboardFilePath ? [storyboardFilePath] : [],
            });

            if (sent) {
              useLayoutStore.getState().setNewLayout(LeftTab.AIChat);
              toast.success(
                "Component pasted. Sending to AI for enhancement...",
              );
            }
          };

          setModalHandlers({
            onPasteOriginal: handlePasteOriginal,
            onCleanWithAI: handleCleanWithAI,
          });
          setIsModalOpen(true);
          return;
        }
      }

      // Valid tempo copy data
      const tempoCopyData: ClipboardData | null =
        await getTempoClipboardData(e);
      let nonShadcnFiles: { [key: string]: string } = {};

      if (tempoCopyData) {
        try {
          await insertClipboardDataIntoCanvas(tempoCopyData);
        } catch (error) {
          // Error already logged and toasted by handleTempoCopyElementPaste
        }
        return;
      }

      // From reactcomponents, different format. Should install dependencies then paste the text as code
      // TODO: Unify this with the above tempo copy/paste logic
      if (
        (tempoCopyData as any)?.component_files ||
        (tempoCopyData as any)?.npm_dependencies
      ) {
        // Process component metadata using the new hook
        const { success, nonShadcnFiles: newNonShadcnFiles } =
          await installDepsForComponentRef.current(tempoCopyData as any);
        nonShadcnFiles = { ...nonShadcnFiles, ...newNonShadcnFiles };

        if (!success) {
          return; // Abort the entire paste operation
        }
      }

      const text = await getPastedText();

      if (text === "") {
        return;
      }

      const { x: mouseX, y: mouseY } = useCanvasStore.getState().canvasMousePos;

      await handlePasteCodeOrHtml({
        text,
        mouseX,
        mouseY,
        actionTextSuffix: "",
        project,
        nonShadcnFiles,
        initializeStoryboard,
        createStoryboard,
        showFancyWarning: true,
        selectedPageId,
      });
    },
    [
      createStoryboard,
      handleImagePaste,
      initializeStoryboard,
      insertClipboardDataIntoCanvas,
      installDepsForComponentRef,
      project,
      selectedPageId,
    ],
  );

  const getPastedText = async () => {
    try {
      const clipboardData = await navigator.clipboard.readText();
      return clipboardData;
    } catch (error) {
      console.error("Error reading clipboard data:", error);
      return "";
    }
  };

  /**
   * Register a paste handler and set ALL non-ref dependencies to any variables used within the handler.
   */
  useEffect(() => {
    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [handlePaste]);

  const getCopyDataForSelectedElements = useGetCopyDataForSelectedElements();
  const getCopyDataForSelectedStoryboards =
    useGetCopyDataForSelectedStoryboards();

  const handleCopy = useCallback(
    async (e) => {
      const target = e.target as HTMLElement;
      if (isTargetTextInput(target)) {
        return;
      }

      if (!canvasRef.current) {
        return;
      }

      let copyData: ClipboardData | null = null;

      // Check for storyboard selection first (when no element is selected inside)
      const storyboardIds = multiselectedStoryboardIds?.length
        ? multiselectedStoryboardIds
        : storyboardIdSelected
          ? [storyboardIdSelected]
          : [];

      // Use storyboard copy when multiple storyboards are selected,
      // OR when a single storyboard is selected with no element inside
      const shouldCopyStoryboards =
        multiselectedStoryboardIds?.length > 1 ||
        (storyboardIds.length > 0 && selectedElement.isEmpty());

      if (shouldCopyStoryboards) {
        copyData = await getCopyDataForSelectedStoryboards(storyboardIds);
      } else if (!selectedElement.isEmpty() && selectedNavTreeNode) {
        // Existing element copy logic
        copyData = await getCopyDataForSelectedElements();
      }

      if (!copyData) {
        return;
      }

      setTempoClipboardData(e, copyData, window.getSelection()?.toString());

      e.preventDefault();
    },
    [
      selectedElement,
      selectedNavTreeNode,
      getCopyDataForSelectedElements,
      getCopyDataForSelectedStoryboards,
      storyboardIdSelected,
      multiselectedStoryboardIds,
    ],
  );

  const handleCut = useCallback(
    async (e) => {
      const target = e.target as HTMLElement;
      if (isTargetTextInput(target)) {
        return;
      }

      if (!canvasRef.current) {
        return;
      }

      await handleCopy(e);

      if (!e.defaultPrevented) {
        return;
      }

      try {
        useCanvasStore.getState().setTriggerDeleteElement(true);
      } catch (err) {
        console.error("Error setting trigger delete element:", err);
      }

      if (e.preventDefault) e.preventDefault();
    },
    [handleCopy],
  );

  useEffect(() => {
    window.addEventListener("copy", handleCopy);
    window.addEventListener("cut", handleCut);

    return () => {
      window.removeEventListener("copy", handleCopy);
      window.removeEventListener("cut", handleCut);
    };
  }, [handleCopy, handleCut]);

  return (
    <PasteOptionsModal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      onPasteOriginal={modalHandlers?.onPasteOriginal || (async () => null)}
      onCleanWithAI={modalHandlers?.onCleanWithAI || (async () => undefined)}
      htmlContent={pastedHtml}
    />
  );
});
